import { LitElement, html } from 'lit-element';

import translate from '../../utils/webcomponents/translate';

import * as transactionLookupApi from './transaction-lookup-api';

class TransactionLookup extends LitElement {
	static get properties() {
		return {
			isLoading: { type: Boolean },
			selectedBroker: { type: String },
			isError: { type: Boolean },
			fields: { type: Array },
			result: { type: Object },
		};
	}

	constructor() {
		super();

		this.isLoading = false;
		this.selectedBroker = '';
		this.isError = false;
		this.result = null;

		this.fields = [{
			label: translate('lookuptransaction', 'isin', 'Isin'),
			id: 'Isin',
			fields: [{ name: 'Isin', type: 'text' }],
		},
		{
			label: translate('lookuptransaction', 'date', 'Date'),
			id: 'TimeStampDate',
			fields: [{ name: 'TimeStampDate', type: 'date', min: '2021-10-01', max: '2021-11-01', value: '2021-10-01' }],
		},
		{
			label: translate('lookuptransaction', 'time', 'Time'),
			id: 'TimeStampHours',
			classNames: 'transaction-lookup__field-time',
			fields: [
				{ name: 'TimeStampHours', type: 'number', placeholder: 'hh', min: 0, max: 23 },
				{ name: 'TimeStampMinutes', type: 'number', placeholder: 'mm', min: 0, max: 59 },
			],
		},
		{
			label: translate('lookuptransaction', 'price', 'Price'),
			id: 'Price',
			fields: [{ name: 'Price', type: 'number', min: 0, step: 0.001 }],
		},
		{
			label: translate('lookuptransaction', 'quantity', 'Quantity'),
			id: 'Quantity',
			fields: [{ name: 'Quantity', type: 'number', min: 1 }],
		}];
	}

	async lookup(form) {
		this.isLoading = true;
		this.isError = false;

		try {
			const data = new URLSearchParams(new FormData(form)).toString();
			const result = await transactionLookupApi.post(data);

			this.result = result;
		} catch (e) {
			this.isError = true;
		} finally {
			this.isLoading = false;
		}
	}

	render() {
		if (this.isError) {
			return this.renderError();
		}

		return this.renderLookup();
	}

	renderLookup() {
		return html`
			<div class="transaction-lookup">
				${this.renderLoader()}
				${this.result ? this.renderResult() : this.renderForm()}
			</div>
		`;
	}

	getBrokerText() {
		if (this.selectedBroker === 'broker') {
			return html`
				<p>${translate('lookuptransaction', 'brokerresulttext1', 'Broker text 1')}</p>

				<p>${translate('lookuptransaction', 'brokerresulttext2', 'Broker text 2')}</p>
				<p>${translate('lookuptransaction', 'brokerresulttext3', 'Broker text 3')}</p>
			`;
		}

		return html`
			<p>${translate('lookuptransaction', 'bnpresulttext1', 'BNP text 1')}</p>
			<p>${translate('lookuptransaction', 'bnpresulttext2', 'BNP text 2')} <a href="mailto:hefboom@bnpparibas.com">hefboom@bnpparibas.com</a> ${translate('lookuptransaction', 'bnpresulttext3', 'BNP text 3')}</p>
			<dl class="transaction-lookup__list">
				<dt>${translate('lookuptransaction', 'isin', 'Isin')}</dt>
				<dd>${this.result.isin}</dd>
				<dt>${translate('lookuptransaction', 'product', 'Produc')}</dt>
				<dd>${this.result.productName}</dd>
				<dt>${translate('lookuptransaction', 'date', 'Date')}</dt>
				<dd>${this.result.transactionDateFormatted}</dd>
				<dt>${translate('lookuptransaction', 'time', 'Time')}</dt>
				<dd>${this.result.transactionTimeFormatted}</dd>
				<dt>${translate('lookuptransaction', 'price', 'Price')}</dt>
				<dd>${this.result.priceFormatted}</dd>
				<dt>${translate('lookuptransaction', 'quantity', 'Quantity')}</dt>
				<dd>${this.result.quantityFormatted}</dd>
			</dl>
		`;
	}

	onChangeBroker({ target }) {
		const { value } = target.options[target.selectedIndex];

		this.selectedBroker = value;
	}

	renderForm() {
		const labelTitle = translate('lookuptransaction', 'title', 'Transaction lookup');
		const labelIntro = translate('lookuptransaction', 'intro', 'Intro text');

		return html`
			<h2>${labelTitle}</h2>
			<p>${labelIntro}</p>
			<hr />
			<div class="transaction-lookup__form">
				<form @submit="${this.onSubmit}" class="form">
					<x-transaction-lookup-form .fields="${this.fields}"></x-transaction-lookup-form>
				</form>
			</div>
		`;
	}

	renderResult() {
		const labelResult = translate('lookuptransaction', 'result', 'Result of lookup');
		const labelSearchAgain = translate('lookuptransaction', 'searchAgain', 'Search again');

		const hasMatches = this.result?.transactionHasMatches;

		return html`
			<h2>${labelResult}</h2>
			<div class="trailer">
				${hasMatches ? this.renderResultMatch() : this.renderResultNoMatch()}
			</div>
			<hr />
			<ul class="stack">
				<li class="stack__item">
					<button type="button" @click="${this.onReset}" class="button button--inverted">${labelSearchAgain}</button>
				</li>
			</ul>
		`;
	}

	renderResultMatch() {
		const labelMatch = translate('lookuptransaction', 'match', 'U heeft een transactie die mogelijk onderdeel uit maakt van de getroffen reeks. Selecteer hieronder de bank of broker alwaar u de transactie heeft uitgevoerd.');

		return html`
			<p>${labelMatch}</p>
			<form @change="${this.onChangeBroker}" class="form text-max-width-s">
				<div class="field">
					<select name="broker" class="field__input">
						<option value="">Selecteer uw broker</option>
						<option value="broker">ABN AMRO</option>
						<option value="broker">BinckBank</option>
						<option value="broker">DeGiro</option>
						<option value="broker">ING</option>
						<option value="broker">LYNX</option>
						<option value="broker">Rabobank</option>
						<option value="broker">Saxo Bank</option>
						<option value="other">Overige banken en brokers </option>
					</select>
				</div>
			</form>
			${this.selectedBroker ? this.getBrokerText() : ''}
		`;
	}

	renderResultNoMatch() {
		const labelNoMatch = translate('lookuptransaction', 'noMatch', 'Based on your input, there is no reason to believe your transaction needs any further attention.');

		return html`
			${labelNoMatch}
		`;
	}

	renderLoader() {
		return this.isLoading
			? html`<x-loader .isLoading="${this.isLoading}"></x-loader>`
			: '';
	}

	renderError() {
		const errorFeedback = translate('lookuptransaction', 'somethingWentWrong', 'Something went wrong. Please try again.');
		const labelRetry = translate('lookuptransaction', 'retry', 'Retry');

		return html`
			<div class="trailer">
				<x-message modifier="error" icon="warning" message="${errorFeedback}"></x-message>
				<button class="button" @click="${this.onReset}">${labelRetry}</button>
			</div>
		`;
	}

	onSubmit(e) {
		e.preventDefault();

		this.lookup(e.target);
	}

	onReset() {
		this.isError = false;
		this.result = null;
		this.selectedBroker = '';
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-transaction-lookup', TransactionLookup);

export default TransactionLookup;
